/**
 * Enumerações de constantes de analytics GA4 — FLUXO_TRADICIONAL e FLUXO_PARCERIAS.
 *
 * Centraliza todos os `custom_path` e identificadores de `click` fixos
 * utilizados no tagueamento da jornada Onboarding Agro PF conforme
 * TechSpec 15213 (FLUXO_TRADICIONAL) e TechSpec 15278 (FLUXO_PARCERIAS).
 *
 * NOTA: Usar `enum` regular (não `const enum`) — incompatível com
 * `isolatedModules: true` do Angular 16 com ngx-build-plus.
 *
 * Valores dinâmicos determinados em runtime (ex.: seleção do usuário no template)
 * NÃO estão incluídos aqui — permanecem como parâmetros variáveis nos componentes.
 *
 * Convenção de sufixos:
 * - Sufixo `Tradicional` → contextos com tela homônima em ambos os fluxos (TechSpec 15213)
 * - Sufixo `Parcerias`   → contextos com tela homônima em ambos os fluxos (TechSpec 15278)
 *
 * @see {@link https://portaltoolkitanalytics.cloud.itau.com.br/techspecGa4?documento=15213}
 * @see {@link https://portaltoolkitanalytics.cloud.itau.com.br/techspecGa4?documento=15278}
 */

// ─────────────────────────────────────────────────────────────────────────────
// COMPARTILHADO — constantes globais presentes em todos os eventos
// ─────────────────────────────────────────────────────────────────────────────

/** Constantes globais do SDK Analytics GA4 — compartilhadas entre ambos os fluxos */
export enum AnalyticsEnumGeneral {
  IMPLEMENTATION_TEAM = 'canais-digitais-agro-ii:jornada-de-dados-e-distribuicao',
}

// ─────────────────────────────────────────────────────────────────────────────
// FLUXO_TRADICIONAL (TechSpec 15213)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Constantes de analytics para o contexto de Hub Proponentes.
 * Cobre a tela: Hub Proponentes (tela-inicial).
 * Exclusivo do FLUXO_TRADICIONAL — sem equivalente no FLUXO_PARCERIAS.
 */
export enum AnalyticsEnumProponentes {
  // Hub Proponentes (tela-inicial)
  HUB_PROPONENTES_CUSTOM_PATH = '/abertura-de-conta-corrente/onboarding-agro-pf-tradicional/tela-inicial',
  HUB_PROPONENTES_CLICK_AVANCAR = 'botao:ir-para-produtos-e-servicos',
  HUB_PROPONENTES_CLICK_ADICIONAR_COTITULAR = 'botao:adicionar-novo-cotitular',
  HUB_PROPONENTES_CLICK_EDITAR = 'botao:editar',
  HUB_PROPONENTES_CLICK_EXCLUIR = 'botao:excluir',
}

/**
 * Constantes de analytics para o contexto de Dados Pessoais — FLUXO_TRADICIONAL.
 * Cobre a tela: Dados Pessoais (dados-pessoais).
 *
 * @see AnalyticsEnumDadosPessoaisParcerias para o equivalente no FLUXO_PARCERIAS.
 */
export enum AnalyticsEnumDadosPessoaisTradicional {
  CUSTOM_PATH = '/abertura-de-conta-corrente/onboarding-agro-pf-tradicional/dados-pessoais',
  CLICK_AVANCAR = 'botao:salvar-e-continuar',
  CLICK_VOLTAR = 'botao:voltar',
  CLICK_SALVAR = 'botao:continuar',
  CLICK_WHATSAPP = 'botao:por-onde-prefere-receber-informacoes-sobre-a-sua-conta-corrente-whatsapp',
  CLICK_EMAIL = 'botao:por-onde-prefere-receber-informacoes-sobre-a-sua-conta-corrente-e-mail',
  CLICK_ENDERECO_COMERCIAL_MESMO = 'botao:qual-seu-endereco-comercial-mesmo-da-residencia',
  CLICK_ENDERECO_COMERCIAL_OUTRO = 'botao:qual-seu-endereco-comercial-outro',
  CLICK_ENDERECO_CORRESPONDENCIA_MESMO = 'botao:qual-seu-endereco-correspondencia-mesmo-da-residencia',
  CLICK_ENDERECO_CORRESPONDENCIA_OUTRO = 'botao:qual-seu-endereco-correspondencia-outro',
}

/**
 * Constantes de analytics para o contexto de Dados Complementares — FLUXO_TRADICIONAL.
 * Cobre a tela: Dados Complementares (dados-complementares).
 *
 * @see AnalyticsEnumDadosComplementaresParcerias para o equivalente no FLUXO_PARCERIAS.
 */
export enum AnalyticsEnumDadosComplementaresTradicional {
  CUSTOM_PATH = '/abertura-de-conta-corrente/onboarding-agro-pf-tradicional/dados-complementares',
  CLICK_AVANCAR = 'botao:ir-para-envio-de-documentos',
  CLICK_VOLTAR = 'botao:voltar',
  CLICK_SALVAR = 'botao:salvar-e-continuar-depois',
  CLICK_POSSUI_PROCURADOR = 'botao:possui-um-procurador',
  CLICK_EXCLUIR_PROCURADOR = 'botao:excluir-procurador',
}

/**
 * Constantes de analytics para o contexto do sidenav de Procurador — FLUXO_TRADICIONAL.
 * Cobre a tela: Sidenav de Procurador (procurador), aberta a partir da seção
 * "Procurador" da tela de Dados Complementares.
 */
export enum AnalyticsEnumProcuradorSidebarTradicional {
  CUSTOM_PATH = '/abertura-de-conta-corrente/onboarding-agro-pf-tradicional/procurador',
  CLICK_CANCELAR = 'botao:cancelar',
  CLICK_ADICIONAR = 'botao:adicionar',
}

/**
 * Constantes de analytics para o contexto de Documentos — FLUXO_TRADICIONAL.
 * Cobre a tela: Envio de Documentos Pessoais (envio-de-documentos-pessoais).
 *
 * @see AnalyticsEnumDocumentosParcerias para o equivalente no FLUXO_PARCERIAS.
 */
export enum AnalyticsEnumDocumentosTradicional {
  CUSTOM_PATH = '/abertura-de-conta-corrente/onboarding-agro-pf-tradicional/envio-de-documentos-pessoais',
  CLICK_AVANCAR = 'botao:continuar',
  CLICK_VOLTAR = 'botao:voltar',
}

/**
 * Constantes de analytics para o contexto de Produtos.
 * Cobre as telas: Produtos e Serviços para Você (selecao-produtos)
 * e Limite de Pagamento Diário (alcada-sispag).
 * Exclusivo do FLUXO_TRADICIONAL.
 */
export enum AnalyticsEnumProdutosTradicional {
  // Seleção de Produtos
  SELECAO_PRODUTOS_CUSTOM_PATH = '/abertura-de-conta-corrente/onboarding-agro-pf-tradicional/produtos-e-servicos-para-voce',
  SELECAO_PRODUTOS_CLICK_AVANCAR = 'botao:-ir-para-limites-de-pagamentos',
  SELECAO_PRODUTOS_CLICK_VOLTAR = 'botao:voltar',
  SELECAO_PRODUTOS_CLICK_SALVAR = 'botao:salvar-e-continuar-depois',

  // Alçada SisPag (Limite de Pagamento Diário)
  ALCADA_SISPAG_CUSTOM_PATH = '/abertura-de-conta-corrente/onboarding-agro-pf-tradicional/limite-de-pagamento-diario',
  ALCADA_SISPAG_CLICK_AVANCAR = 'botao:ir-para-assinatura-da-proposta',
  ALCADA_SISPAG_CLICK_VOLTAR = 'botao:voltar',
  ALCADA_SISPAG_CLICK_SALVAR = 'botao:salvar-e-continuar-depois',
  ALCADA_SISPAG_CLICK_DEFINIR_VALOR_MAXIMO = 'botao:definir-valor-maximo',
}

/**
 * Constantes de analytics para o contexto de Assinatura — FLUXO_TRADICIONAL.
 * Cobre as telas: Tipos de Assinatura da Proposta (tipo-assinatura),
 * Assinatura (assinatura — multi-variação) e Envio da Proposta (hub-assinatura).
 *
 * NOTA: A tela `assinatura` pode ser renderizada por múltiplos componentes
 * dependendo do tipo de assinatura escolhido. Todas as variações disparam
 * `screen_view` com o mesmo `ASSINATURA_CUSTOM_PATH` — conforme TechSpec 15213:
 * "tela similar vai aparecer dependendo da escolha de assinatura, deve-se taguear todas as chamadas".
 *
 * @see AnalyticsEnumAssinaturaParcerias para o equivalente no FLUXO_PARCERIAS.
 */
export enum AnalyticsEnumAssinaturaTradicional {
  // Tipo de Assinatura
  TIPO_ASSINATURA_CUSTOM_PATH = '/abertura-de-conta-corrente/onboarding-agro-pf-tradicional/tipos-de-assinatura-da-proposta',
  TIPO_ASSINATURA_CLICK_AVANCAR = 'botao:continuar',
  TIPO_ASSINATURA_CLICK_VOLTAR = 'botao:voltar',
  TIPO_ASSINATURA_CLICK_DOCUMENTO_FISICO = 'botao:documento-fisico',
  TIPO_ASSINATURA_CLICK_ASSINATURA_DIGITAL_E_CPF = 'botao:assinatura-digital-e-cpf',
  TIPO_ASSINATURA_CLICK_ASSINATURA_ELETRONICA = 'botao:assinatura-eletronica',

  // Assinatura (multi-variação)
  ASSINATURA_CUSTOM_PATH = '/abertura-de-conta-corrente/onboarding-agro-pf-tradicional/assinatura',
  ASSINATURA_CLICK_ALTERAR = 'botao:alterar-tipo-de-assinatura',

  // Hub Assinatura (Envio da Proposta)
  HUB_ASSINATURA_CUSTOM_PATH = '/abertura-de-conta-corrente/onboarding-agro-pf-tradicional/envio-da-proposta',
  HUB_ASSINATURA_CLICK_AVANCAR = 'botao:enviar-para-analise',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  HUB_ASSINATURA_CLICK_VOLTAR = 'botao:alterar-tipo-de-assinatura',
}

/**
 * Constantes de analytics para o contexto de Assinatura Eletrônica — FLUXO_TRADICIONAL.
 * Cobre a tela: Assinatura Eletrônica (assinatura-eletronica), exibida no hub de
 * assinatura quando o tipo de assinatura escolhido é "eletronica".
 */
export enum AnalyticsEnumAssinaturaEletronicaTradicional {
  CUSTOM_PATH = '/abertura-de-conta-corrente/onboarding-agro-pf-tradicional/assinatura-eletronica',
  CLICK_ASSINATURA_TITULAR = 'botao:assinatura-titular',
  CLICK_ASSINATURA_COTITULAR = 'botao:assinatura-cotitular',
  CLICK_COPIAR_LINK_COTITULAR = 'botao:copiar-link-de-assinatura-cotitular',
  CLICK_ALTERAR_TIPO_ASSINATURA = 'botao:alterar-tipo-de-assinatura-eletronica',
  CLICK_ATUALIZAR_PAGINA = 'botao:atualizar-a-pagina',
}

/**
 * Constantes de analytics para o contexto de Assinatura Digital (e-CPF) — FLUXO_TRADICIONAL.
 * Cobre a tela: Assinatura Digital e-CPF (assinatura-digital-ecpf), exibida no hub de
 * assinatura quando o tipo de assinatura escolhido é "ecpf".
 */
export enum AnalyticsEnumAssinaturaDigitalEcpfTradicional {
  CUSTOM_PATH = '/abertura-de-conta-corrente/onboarding-agro-pf-tradicional/assinatura-digital-ecpf',
  CLICK_ASSINATURA_TITULAR = 'botao:assinatura-titular',
  CLICK_ASSINATURA_COTITULAR = 'botao:assinatura-cotitular',
  CLICK_COPIAR_LINK_COTITULAR = 'botao:copiar-link-de-assinatura-cotitular',
  CLICK_ALTERAR_TIPO_ASSINATURA = 'botao:alterar-tipo-de-assinatura-digital-e-cpf',
  CLICK_ATUALIZAR_PAGINA = 'botao:atualizar-a-pagina',
}

// ─────────────────────────────────────────────────────────────────────────────
// FLUXO_PARCERIAS (TechSpec 15278)
// Enums criados para uso futuro — componentes Parcerias serão implementados
// em história separada. Valores extraídos da TechSpec 15278 em 2026-06-11.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Constantes de analytics para o contexto de Dados Pessoais — FLUXO_PARCERIAS.
 * Cobre a tela: Dados Pessoais (dados-pessoais) do fluxo Parcerias.
 *
 * @see AnalyticsEnumDadosPessoaisTradicional para o equivalente no FLUXO_TRADICIONAL.
 */
export enum AnalyticsEnumDadosPessoaisParcerias {
  CUSTOM_PATH = '/abertura-de-conta-corrente/onboarding-agro-pf-parcerias/dados-pessoais',
  CLICK_CONTINUAR = 'botao:continuar',
  CLICK_SALVAR_CONTINUAR_DEPOIS = 'botao:salvar-e-continuar-depois',
  CLICK_WHATSAPP = 'botao:whatsapp',
  CLICK_EMAIL = 'botao:e-mail',
}

/**
 * Constantes de analytics para o contexto de Dados Complementares — FLUXO_PARCERIAS.
 * Cobre a tela: Dados Complementares (dados-complementares) do fluxo Parcerias.
 *
 * NOTA: `CLICK_SALVAR_CONTINUAR_DEPOIS` preserva o typo `conitnuar` (sem 'n' entre 'o' e 't') —
 * valor vigente na TechSpec 15278. Não corrigir para evitar regressão de tagueamento.
 *
 * @see AnalyticsEnumDadosComplementaresTradicional para o equivalente no FLUXO_TRADICIONAL.
 */
export enum AnalyticsEnumDadosComplementaresParcerias {
  CUSTOM_PATH = '/abertura-de-conta-corrente/onboarding-agro-pf-parcerias/dados-complementares',
  CLICK_IR_PARA_DOCUMENTOS = 'botao:ir-para-envio-de-documentos',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  CLICK_SALVAR_CONTINUAR_DEPOIS = 'botao:salvar-e-conitnuar-depois', // typo intencional — TechSpec 15278
  CLICK_VOLTAR = 'botao:voltar',
}

/**
 * Constantes de analytics para o contexto de Documentos — FLUXO_PARCERIAS.
 * Cobre a tela: Envio de Documentos Pessoais (envio-de-documentos-pessoais) do fluxo Parcerias.
 *
 * @see AnalyticsEnumDocumentosTradicional para o equivalente no FLUXO_TRADICIONAL.
 */
export enum AnalyticsEnumDocumentosParcerias {
  CUSTOM_PATH = '/abertura-de-conta-corrente/onboarding-agro-pf-parcerias/envio-de-documentos-pessoais',
  CLICK_CONTINUAR = 'botao:continuar',
  CLICK_VOLTAR = 'botao:voltar',
  CLICK_SUBSTITUIR = 'botao:substituir',
  CLICK_EXCLUIR = 'botao:excluir',
}

/**
 * Constantes de analytics para o contexto de Assinatura — FLUXO_PARCERIAS.
 * Cobre a tela: Assinatura (assinatura) do fluxo Parcerias.
 *
 * @see AnalyticsEnumAssinaturaTradicional para o equivalente no FLUXO_TRADICIONAL.
 */
export enum AnalyticsEnumAssinaturaParcerias {
  CUSTOM_PATH = '/abertura-de-conta-corrente/onboarding-agro-pf-parcerias/assinatura',
  CLICK_ATUALIZAR_PAGINA = 'botao:atualizar-a-pagina',
  CLICK_ABRIR_PAC_TITULAR = 'botao:abrir-pac-titular',
  CLICK_ASSINAR_PROPOSTA = 'botao:assinar-proposta',
  CLICK_ASSINAR_CONCLUIR = 'botao:assinar-e-concluir',
  CLICK_VOLTAR = 'botao:voltar',
  CLICK_ENVIAR_LINK = 'botao:enviar-link',
}
